/**
 * Pure domain routing data + derived origin lists. NO `#lib`/`$env` imports, so
 * this module stays safe to import from client, server, and plain Node code.
 *
 * siteConfig objects are attached in `domains.ts` (they pull in `$env` via the
 * site configs) — keep this file dependency-free.
 */

export const domainRouting = {
	"app.fergmar.com": {
		entry: ["localhost", "app.fergmar.com"],
		destination: null,
		faviconSlug: "fergmar-information-terminal"
	},
	"go.fergmar.com": {
		entry: ["go.fergmar.localhost", "go.fergmar.com"],
		destination: "/go-fergmar-com",
		faviconSlug: "fergmar"
	},
	"fergmar.com": {
		entry: ["fergmar.localhost", "fergmar.com", "www.fergmar.com"],
		destination: "/fergmar-com",
		faviconSlug: "fergmar"
	},
	"fergmarcapital.com": {
		entry: ["fergmarcapital.localhost", "fergmarcapital.com", "www.fergmarcapital.com"],
		destination: "/fergmarcapital-com",
		hasBlog: true,
		faviconSlug: "fergmar-capital"
	},
	"fergmarproperties.com": {
		entry: [
			"fergmarproperties.localhost",
			"fergmarproperties.com",
			"www.fergmarproperties.com"
		],
		destination: "/fergmarproperties-com",
		faviconSlug: "fergmar"
	},
	"brand.fergmar.com": {
		entry: ["brand.localhost", "brand.fergmar.com", "www.brand.fergmar.com"],
		destination: "/brand",
		faviconSlug: "fergmar"
	},
	"elevationonairline.com": {
		entry: [
			"elevationonairline.localhost",
			"elevationonairline.com",
			"www.elevationonairline.com"
		],
		destination: "/rental-template/elevationonairline-com",
		faviconSlug: "elevation-on-airline"
	},
	"thevillastx.com": {
		entry: ["thevillastx.localhost", "thevillastx.com", "www.thevillastx.com"],
		destination: "/rental-template/thevillastx-com",
		faviconSlug: "the-villas"
	},
	"liveatparkwood.com": {
		entry: ["liveatparkwood.localhost", "liveatparkwood.com", "www.liveatparkwood.com"],
		destination: "/rental-template/liveatparkwood-com",
		faviconSlug: "parkwood"
	},
	"fergmarhomebuyers.com": {
		entry: [
			"fergmarhomebuyers.localhost",
			"fergmarhomebuyers.com",
			"www.fergmarhomebuyers.com"
		],
		destination: "/fergmarhomebuyers-com",
		hasBlog: true,
		faviconSlug: "fergmar-home-buyers"
	}
} as const;

export const blogSiteSlugs = Object.values(domainRouting).flatMap((site) =>
	"hasBlog" in site && site.destination ? [site.destination.slice(1)] : []
);

/** Valid site keys for team member display filtering. */
export type DisplaySite = keyof typeof domainRouting;

/** Array of all valid display site keys for UI iteration. */
export const displaySiteKeys = Object.keys(domainRouting) as DisplaySite[];

/** Every hostname registered across the routing map (deduped). */
const allEntryHostnames = [
	...new Set(Object.values(domainRouting).flatMap((entry) => entry.entry))
];

const isLocalhostHost = (hostname: string) => hostname.endsWith("localhost");

/**
 * Add the `www.` variant of each host defensively. www is redirected to the apex
 * at Cloudflare, but we trust both in case a request reaches the origin directly —
 * and this guarantees coverage for future domains without per-entry `www.` upkeep.
 */
const withWwwVariants = (hosts: string[]) => [
	...new Set(hosts.flatMap((host) => (host.startsWith("www.") ? [host] : [host, `www.${host}`])))
];

/**
 * Trusted origins for our own domains, derived from the routing map so every
 * registered domain is covered uniformly. Used for the CORS allow-list and the
 * same-origin check in `hooks.server.ts`.
 *
 * `https://` for every non-localhost host (+ defensive `www.` variants).
 */
export const corsProdOrigins = withWwwVariants(
	allEntryHostnames.filter((hostname) => !isLocalhostHost(hostname))
).map((hostname) => `https://${hostname}`);

const devLocalhostHosts = new Set<string>(allEntryHostnames.filter(isLocalhostHost));

/**
 * Dev CORS: trust any port on our `*.localhost` hosts. vite picks a free port per
 * worktree (no fixed dev port), so match host, not port. Dev-only — CSRF is skipped
 * in dev, and this keeps the module `$env`-free / client-safe.
 */
export const isDevOrigin = (origin: string): boolean => {
	try {
		const { protocol, hostname } = new URL(origin);
		return protocol === "http:" && devLocalhostHosts.has(hostname);
	} catch {
		return false;
	}
};
